import styled from '@emotion/styled';
import { App, Switch } from 'antd';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API } from '../api';
import { UserInfo } from '../components/account/user-info';
import { UserMenu } from '../components/account/user-menu';
import { PageHeader } from '../components/common/page-header';
import { LocaleKeys } from '../locale';
import { useAppDispatch } from '../state';
import { useMeSelector } from '../state/me/selectors';
import { meActions } from '../state/me/slice';
import {
  DEBUG_VIDEO_PLAYER_LS_KEY,
  isDebugVideoPlayerEnabled,
} from '../utils/debug-video-player';

const Wrapper = styled('div')`
  min-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 100px;
`;

const Inner = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DataWrapper = styled('div')``;

const StudentAccessRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 24px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 15px;
  font-weight: 600;
`;

export const Account: FC = () => {
  const { t } = useTranslation([LocaleKeys.ACCOUNT]);
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const { meData } = useMeSelector();
  const [studentAccessPending, setStudentAccessPending] = useState(false);
  const [debugVideoPlayer, setDebugVideoPlayer] = useState(
    isDebugVideoPlayerEnabled
  );

  const handleStudentAccessChange = async (studentAccess: boolean) => {
    if (!meData) return;

    setStudentAccessPending(true);
    try {
      const response = await API.users.updatePermissions(meData.id, {
        studentAccess,
      });
      dispatch(
        meActions.finishMe({
          ...meData,
          studentAccess: response.data.studentAccess,
        })
      );
      message.success('Student access updated');
    } catch {
      message.error('Student access update failed');
    } finally {
      setStudentAccessPending(false);
    }
  };

  const handleDebugVideoPlayerChange = (enabled: boolean) => {
    setDebugVideoPlayer(enabled);

    if (enabled) {
      localStorage.setItem(DEBUG_VIDEO_PLAYER_LS_KEY, 'true');
      message.success('Playback debug logging enabled');
      return;
    }

    localStorage.removeItem(DEBUG_VIDEO_PLAYER_LS_KEY);
    message.success('Playback debug logging disabled');
  };

  return (
    <Wrapper>
      <PageHeader>{t('header.account')}</PageHeader>
      <Inner>
        <DataWrapper>
          <UserInfo />
          <UserMenu />
          {meData?.role === 3 && (
            <StudentAccessRow>
              <span>Student access</span>
              <Switch
                checked={meData.studentAccess}
                loading={studentAccessPending}
                disabled={studentAccessPending}
                onChange={(checked) =>
                  void handleStudentAccessChange(checked)
                }
              />
            </StudentAccessRow>
          )}
          <StudentAccessRow>
            <span>Playback debug logging</span>
            <Switch
              checked={debugVideoPlayer}
              onChange={handleDebugVideoPlayerChange}
            />
          </StudentAccessRow>
        </DataWrapper>
      </Inner>
    </Wrapper>
  );
};
